import { z } from "zod";
const optionalHttpsUrlSchema = z.string().trim().url().max(2_048).refine((value) => {
    try {
        return new URL(value).protocol === "https:";
    }
    catch {
        return false;
    }
}, "Organization member image URL must use HTTPS.").optional();
export const organizationRoleSchema = z.enum(["org:member", "org:admin"]);
/** Normalize supported identity-provider aliases; unknown/custom roles grant no implicit authority. */
export function normalizeOrganizationRole(role) {
    if (role === "org:admin" || role === "admin" || role === "owner")
        return "org:admin";
    if (role === "org:member" || role === "member")
        return "org:member";
    return null;
}
export function isOrganizationAdminRole(role) {
    return normalizeOrganizationRole(role) === "org:admin";
}
/**
 * Guests are people outside the organisation invited into some of its
 * channels. A View guest reads and talks; an Edit guest can also change
 * records and files. Each role is a person's, not a channel's.
 */
export const organizationGuestRoleSchema = z.enum(["org:guest_viewer", "org:guest_editor"]);
export const guestAccessSchema = z.enum(["view", "edit"]);
/** The access a guest role grants, or null for every other role. */
export function organizationGuestAccess(role) {
    if (role === "org:guest_viewer")
        return "view";
    if (role === "org:guest_editor")
        return "edit";
    return null;
}
export const organizationManagementCapabilitiesSchema = z.object({
    canManageMembers: z.boolean(),
    canManageProfile: z.boolean(),
});
const organizationIconColorSchema = z.string().regex(/^#[0-9a-f]{6}$/iu, "Organization icon colors must be six-digit hex values.");
export const organizationIconSchema = z.object({
    style: z.enum(["letter", "logo"]),
    backgroundColor: organizationIconColorSchema,
    textColor: organizationIconColorSchema,
    letterScale: z.number().min(0.5).max(1),
});
export const ORGANIZATION_ICON_METADATA_KEY = "mlxOrganizationIcon";
export function organizationIconFromPublicMetadata(value) {
    if (!value || typeof value !== "object" || Array.isArray(value))
        return undefined;
    const metadata = value;
    const parsed = organizationIconSchema.safeParse(metadata[ORGANIZATION_ICON_METADATA_KEY]);
    return parsed.success ? parsed.data : undefined;
}
export const organizationMemberSchema = z.object({
    id: z.string().trim().min(1).max(240),
    userId: z.string().trim().min(1).max(240),
    externalUserId: z.string().trim().min(1).max(240).optional(),
    email: z.string().trim().email().optional(),
    name: z.string().trim().min(1).max(240).optional(),
    imageUrl: optionalHttpsUrlSchema,
    role: z.string().trim().min(1).max(160).optional(),
    joinedAt: z.number().int().nonnegative().optional(),
    isCurrentUser: z.boolean().default(false),
});
export const organizationMemberDirectorySchema = z.object({
    members: z.array(organizationMemberSchema).max(100),
    totalCount: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().min(1).max(100),
    capabilities: organizationManagementCapabilitiesSchema,
});
/** Fresh membership for the signed subject; organisationId is the directory scope, before warehouse aliases. */
export const currentOrganizationMembershipSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(240),
    organisationId: z.string().trim().min(1).max(240),
    userId: z.string().trim().min(1).max(240),
    externalUserId: z.string().trim().min(1).max(240),
    role: z.string().trim().min(1).max(160).nullable(),
});
export const organizationSummarySchema = z.object({
    id: z.string().trim().min(1).max(240),
    name: z.string().trim().min(1).max(240),
    imageUrl: optionalHttpsUrlSchema,
    icon: organizationIconSchema.optional(),
    memberCount: z.number().int().nonnegative(),
    maxAllowedMemberships: z.number().int().positive().nullable(),
    capabilities: organizationManagementCapabilitiesSchema,
});
export const organizationInvitationSchema = z.object({
    id: z.string().trim().min(1).max(240),
    emailAddress: z.string().trim().email(),
    role: z.string().trim().min(1).max(160),
    status: z.string().trim().min(1).max(80),
    createdAt: z.number().int().nonnegative(),
    expiresAt: z.number().int().nonnegative().optional(),
});
export const organizationInvitationDirectorySchema = z.object({
    invitations: z.array(organizationInvitationSchema).max(100),
    totalCount: z.number().int().nonnegative(),
    offset: z.number().int().nonnegative(),
    limit: z.number().int().min(1).max(100),
    capabilities: organizationManagementCapabilitiesSchema,
});
export const organizationNameUpdateSchema = z.object({
    name: z.string().trim().min(1).max(100),
});
export const organizationIconUpdateSchema = z.object({
    icon: organizationIconSchema,
});
export const organizationMemberRoleUpdateSchema = z.object({
    membershipId: z.string().trim().min(1).max(240),
    role: organizationRoleSchema,
});
export const organizationMemberRemovalSchema = z.object({
    membershipId: z.string().trim().min(1).max(240),
    userId: z.string().trim().min(1).max(240),
    externalUserId: z.string().trim().min(1).max(240),
    reason: z.string().trim().min(8).max(500),
    controlPlaneImpactSha256: z.string().regex(/^[0-9a-f]{64}$/u),
    oidcImpactSha256: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
});
